// React
import { useCallback } from 'react';
import { useLoaderData } from 'react-router-dom';

// Hooks
import { usePreviewData } from '@/hooks/usePreviewData';

// Utils
import { getSlug } from '@/utils/slug';

// Services
import { getHeader } from '@/services/header/headerService';
import { getFooter } from '@/services/footer/footerService';
import { getArticle } from '@/services/articles/articlesService';

// Components
import LayoutHeaderFooter from '@/components/layout/LayoutHeaderFooter/LayoutHeaderFooter';
import { PageBlocks } from '@/components/blocks/Blocks';

// Styles
import { Helmet } from 'react-helmet';
import { RoutesEnum } from '@/enum/RoutesEnum';
import styles from './Blog.module.css';

export async function articleLoader({ request }: { request: Request }) {
  const slug = getSlug(new URL(request.url));

  if (!slug) return null;

  const [header, footer, article] = await Promise.all([
    getHeader(),
    getFooter(),
    getArticle(slug),
  ]);

  return {
    header,
    footer,
    article,
  };
}

function Article() {
  const loaderData = useLoaderData() as Awaited<ReturnType<typeof articleLoader>>;
  const refetch = useCallback(
    () => articleLoader({ request: new Request(window.location.href) }),
    [],
  );
  const data = usePreviewData(loaderData, refetch);
  const baseUrl = import.meta.env.VITE_DOMAIN;

  if (!data) return null;

  const { header, footer, article } = data;

  if (!article) return null;

  return (
    <main>
      <Helmet>
        <title>{article.title}</title>
        {article.description ? <meta name="description" content={article.description} /> : null}
        <link rel="canonical" href={`${baseUrl}/${RoutesEnum.BLOG}/${article.slug}`} />
      </Helmet>
      <LayoutHeaderFooter header={header} footer={footer}>
        <div className={styles.body}>
          <PageBlocks blocks={article.blocks} />
        </div>
      </LayoutHeaderFooter>
    </main>
  );
}

export default Article;
